/* Animated circuit background - beam layer over the static tile.
   Zero dependencies. All geometry comes from window.CIRCUIT_BG_DATA. */
(function () {
  'use strict';

  /* Loading the file twice (two Shopify sections both including the asset) would
     otherwise give every element two canvases and two rAF loops. */
  if (window.CircuitBackground && window.CircuitBackground.version === 2) return;

  var TRAIL_LEN = 90;        /* longest trail, image px behind the head, scaled by the tile scale */
  var TRAIL_MIN = 24;        /* shortest trail; between them the trail is TRAIL_FRAC of the route */
  var TRAIL_FRAC = 0.5;
  var TRAIL_STEPS = 18;
  var TRAIL_MAX = 56;
  var CORE_MIN = 1.6;        /* core stroke floor; the trace's own width wins above it */
  var GLOW_EXTRA = 5;        /* glow stroke = core + this */
  var GLOW_A = 0.35;
  var BLOOM_EXTRA = 15;      /* outer halo = core + this */
  var BLOOM_A = 0.12;
  var HEAD_R = 7;            /* head bloom radius, image px */
  var HEAD_A = 0.55;
  var FLASH_MS = 700;
  var FLASH_MAX = 40;
  var FLASH_R_MUL = 3;       /* flash radius = 3 * pad r + 7, image px: the extracted */
  var FLASH_R_ADD = 7;       /* pads are r 1-3, so 3r alone is invisible in situ */
  var DUR_MIN = 2;           /* s for one traverse of a route, before data-speed */
  var DUR_MAX = 4;
  var SPEED_MIN = 12;        /* the duration turned into image px/s is clamped here, */
  var SPEED_MAX = 200;       /* so a 6px stub does not crawl nor a long route tear */
  var SPAWN_MIN = 150;
  var SPAWN_MAX = 700;
  var BEAMS_DEFAULT = 5;
  var BEAMS_NARROW = 3;      /* under NARROW_W the same count reads as a swarm */
  var NARROW_W = 640;
  var BEAMS_MAX = 60;
  var NODE_SNAP = 4;         /* trace ends within this of each other are one node */
  var PAD_SNAP = 6;          /* an end within pad.r + this joins that pad's node: the
                                extractor insets every trace ~3px from the pad edge */
  var START_MIN = 6;         /* image px: a beam never starts on a shorter trace */
  var WALK_MIN = 150;        /* target walk length, drawn uniformly per beam */
  var WALK_MAX = 400;
  var WALK_FLOOR = 60;       /* keep re-planning until a route is at least this long */
  var WALK_TRIES = 8;        /* ... or this many attempts have been made */
  var ROUTE_MAX = 24;        /* traces per walk */
  var ROUTE_PTS = 256;       /* points per walk */
  var SCALE_MIN = 0.5;       /* floor for the automatic tile scale */
  var STEP = 1 / 30;         /* update sub-step, s */
  var DT_MAX = 0.1;          /* one frame after a long pause is worth 100ms, no more */
  var FALLBACK_GLOW = '#5fe6ff';       /* used when the data carries no pad bands */
  var FALLBACK_RGB = [95, 230, 255];   /* the same cyan, for an unparseable color */

  var trailBuf = new Float32Array(TRAIL_MAX * 3);   /* x, y, fade - reused every draw */
  /* walk planning scratch: a rejected route must not touch the beam it was for */
  var tryTi = new Int32Array(ROUTE_MAX), tryDir = new Int8Array(ROUTE_MAX);
  var bestTi = new Int32Array(ROUTE_MAX), bestDir = new Int8Array(ROUTE_MAX);
  var walkSeen = new Int32Array(ROUTE_MAX);

  var geomCache = new WeakMap();   /* keyed on the data object: swapping boards re-derives */
  var instances = new WeakMap();
  var live = [];             /* strong list, swept for detached elements on every init */
  var sharedBound = false;
  var motionMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  /* ---------- geometry ---------- */

  /* Trace ends that meet are the junctions a beam can turn at. Pads are the hubs:
     an end that lands on one joins that pad's node, so several traces arriving at
     the same pad from slightly different points still share a node. */
  function buildNodes(data, g) {
    var traces = data.traces || [], pads = data.pads || [];
    var maxR = 0, i;
    for (i = 0; i < pads.length; i++) if (pads[i].r > maxR) maxR = pads[i].r;
    var padCell = Math.max(NODE_SNAP, maxR + PAD_SNAP);   /* never smaller than the search radius */
    var padGrid = new Map();
    for (i = 0; i < pads.length; i++) {
      var k = Math.floor(pads[i].x / padCell) + ',' + Math.floor(pads[i].y / padCell);
      var b = padGrid.get(k);
      if (!b) padGrid.set(k, b = []);
      b.push(i);
    }
    var padNode = new Int32Array(pads.length).fill(-1);
    var freeGrid = new Map();
    var nodes = g.nodes;

    function nearestPad(x, y) {
      var best = -1, bestD = Infinity;
      var cx = Math.floor(x / padCell), cy = Math.floor(y / padCell);
      for (var gx = cx - 1; gx <= cx + 1; gx++) {
        for (var gy = cy - 1; gy <= cy + 1; gy++) {
          var bin = padGrid.get(gx + ',' + gy);
          if (!bin) continue;
          for (var j = 0; j < bin.length; j++) {
            var p = pads[bin[j]], dx = p.x - x, dy = p.y - y;
            var d = Math.sqrt(dx * dx + dy * dy);
            if (d <= p.r + PAD_SNAP && d < bestD) { bestD = d; best = bin[j]; }
          }
        }
      }
      return best;
    }

    function nodeAt(x, y) {
      var pi = nearestPad(x, y);
      if (pi >= 0) {
        if (padNode[pi] < 0) {
          padNode[pi] = nodes.length;
          nodes.push({ x: pads[pi].x, y: pads[pi].y, pad: pads[pi], ts: [] });
        }
        return padNode[pi];
      }
      var cx = Math.floor(x / NODE_SNAP), cy = Math.floor(y / NODE_SNAP);
      for (var gx = cx - 1; gx <= cx + 1; gx++) {
        for (var gy = cy - 1; gy <= cy + 1; gy++) {
          var bin = freeGrid.get(gx + ',' + gy);
          if (!bin) continue;
          for (var j = 0; j < bin.length; j++) {
            var n = nodes[bin[j]], dx = n.x - x, dy = n.y - y;
            if (dx * dx + dy * dy <= NODE_SNAP * NODE_SNAP) return bin[j];
          }
        }
      }
      var id = nodes.length;
      nodes.push({ x: x, y: y, pad: null, ts: [] });
      var key = cx + ',' + cy, cell = freeGrid.get(key);
      if (!cell) freeGrid.set(key, cell = []);
      cell.push(id);
      return id;
    }

    for (i = 0; i < traces.length; i++) {
      var pts = g.pts[i], m = g.cum[i].length, wrap = traces[i].wrap || {};
      if (m < 2) { g.nodeA[i] = -1; g.nodeB[i] = -1; continue; }
      g.nodeA[i] = wrap.start ? -1 : nodeAt(pts[0], pts[1]);
      g.nodeB[i] = wrap.end ? -1 : nodeAt(pts[m * 2 - 2], pts[m * 2 - 1]);
      if (g.nodeA[i] >= 0) nodes[g.nodeA[i]].ts.push(i);
      if (g.nodeB[i] >= 0 && g.nodeB[i] !== g.nodeA[i]) nodes[g.nodeB[i]].ts.push(i);
    }
  }

  function prepare(data) {
    var cached = geomCache.get(data);
    if (cached) return cached;
    var traces = data.traces || [];
    var n = traces.length;
    var g = {
      n: n,
      pts: new Array(n),           /* flat x,y per trace */
      cum: new Array(n),           /* cumulative arc length per point */
      total: new Float64Array(n),
      core: new Float64Array(n),   /* core stroke width from the trace's own width */
      nodeA: new Int32Array(n),    /* node at points[0], -1 when that end wraps */
      nodeB: new Int32Array(n),
      nodes: [],
      starts: [],                  /* traces long enough to start a walk on */
      startCum: null,
      startSum: 0,
      tileW: data.width,
      tileH: data.height
    };
    for (var i = 0; i < n; i++) {
      var p = traces[i].points || [], m = p.length;
      var flat = new Float64Array(Math.max(m, 1) * 2);
      var cum = new Float64Array(Math.max(m, 1));
      if (m > 0) { flat[0] = p[0][0]; flat[1] = p[0][1]; }
      for (var j = 1; j < m; j++) {
        flat[j * 2] = p[j][0];
        flat[j * 2 + 1] = p[j][1];
        var dx = flat[j * 2] - flat[j * 2 - 2], dy = flat[j * 2 + 1] - flat[j * 2 - 1];
        cum[j] = cum[j - 1] + Math.sqrt(dx * dx + dy * dy);
      }
      g.pts[i] = flat;
      g.cum[i] = cum;
      g.total[i] = m > 1 ? cum[m - 1] : 0;
      g.core[i] = Math.max(CORE_MIN, traces[i].width || 2);
      if (g.total[i] >= START_MIN) { g.startSum += g.total[i]; g.starts.push(i); }
    }
    g.startCum = new Float64Array(g.starts.length);
    var acc = 0;
    for (i = 0; i < g.starts.length; i++) { acc += g.total[g.starts[i]]; g.startCum[i] = acc; }
    buildNodes(data, g);
    geomCache.set(data, g);
    return g;
  }

  function round2(v) { return Math.round(v * 100) / 100; }
  function rand(a, b) { return a + Math.random() * (b - a); }

  /* Length-weighted start: a long trace is as likely to be picked as the stubs that
     add up to it, so the eye is not dragged to wherever the fragments are densest. */
  function pickStart(g) {
    var k = g.starts.length;
    if (!k) return -1;
    var r = Math.random() * g.startSum;
    var lo = 0, hi = k - 1;
    while (lo < hi) {
      var mid = (lo + hi) >> 1;
      if (g.startCum[mid] > r) hi = mid; else lo = mid + 1;
    }
    return g.starts[lo];
  }

  /* Fill trailBuf with points walking back TRAIL_LEN from the head of a route.
     Returns the point count; index 0 is the head, fade falls 1 -> 0 to the tail. */
  function buildTrail(px, cum, np, sHead, trailLen) {
    if (!(trailLen > 0) || np < 2) return 0;
    var total = cum[np - 1];
    if (sHead > total) sHead = total;
    if (sHead < 0) sHead = 0;
    var sEnd = sHead - trailLen;
    if (sEnd < 0) sEnd = 0;
    var maxStep = trailLen / TRAIL_STEPS;
    var s = sHead, c = 0, k = 0;
    while (c < TRAIL_MAX) {
      while (k < np - 2 && cum[k + 1] < s) k++;
      while (k > 0 && cum[k] > s) k--;
      var seg = cum[k + 1] - cum[k];
      var t = seg > 0 ? (s - cum[k]) / seg : 0;
      if (t < 0) t = 0; else if (t > 1) t = 1;
      var i0 = k * 2, i1 = i0 + 2, o = c * 3;
      trailBuf[o] = px[i0] + (px[i1] - px[i0]) * t;
      trailBuf[o + 1] = px[i0 + 1] + (px[i1 + 1] - px[i0 + 1]) * t;
      var f = 1 - (sHead - s) / trailLen;
      trailBuf[o + 2] = f > 0 ? f : 0;
      c++;
      if (s <= sEnd) break;
      var ns = s - maxStep;
      if (cum[k] < s && cum[k] > ns) ns = cum[k];   /* land on bends so the trail follows them */
      if (ns < sEnd) ns = sEnd;
      s = ns;
    }
    return c;
  }

  /* ---------- colors ---------- */

  function parseRGB(css) {
    var probe = document.createElement('canvas').getContext('2d');
    probe.fillStyle = '#000';
    probe.fillStyle = css;
    var v = probe.fillStyle;
    if (v.charAt(0) === '#') {
      var h = v.slice(1);
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      var i = parseInt(h, 16);
      return [(i >> 16) & 255, (i >> 8) & 255, i & 255];
    }
    var m = v.match(/(\d+(?:\.\d+)?)/g);
    return m ? [+m[0] | 0, +m[1] | 0, +m[2] | 0] : null;
  }

  /* A beam is light, not paint: keep the hue sampled from the board but lift it to
     full brightness, so the dim extracted pad color still reads as a glow. */
  function beamColor(css) {
    var rgb = css ? parseRGB(css) : null;
    var max = rgb ? Math.max(rgb[0], rgb[1], rgb[2]) : 0;
    if (max < 8) return FALLBACK_RGB;          /* unparseable, or too dark to lift */
    var k = 255 / max;
    return [Math.round(rgb[0] * k), Math.round(rgb[1] * k), Math.round(rgb[2] * k)];
  }

  /* A beam is the light the bright pads are lit by, so it takes its hue from the
     brightest pad band - not from colors.pad, which is the dim blue of an unlit pad. */
  function padGlow(data) {
    var bands = (data.colors && data.colors.padBands) || [];
    var best = null, bestV = -1;
    for (var i = 0; i < bands.length; i++) {
      var rgb = bands[i] && bands[i].color ? parseRGB(bands[i].color) : null;
      if (!rgb) continue;
      var v = rgb[0] + rgb[1] + rgb[2];
      if (v > bestV) { bestV = v; best = bands[i].color; }
    }
    return best || FALLBACK_GLOW;
  }

  function makeFlashSprite(rgb) {
    var c = document.createElement('canvas');
    c.width = c.height = 64;
    var x = c.getContext('2d');
    var col = rgb[0] + ',' + rgb[1] + ',' + rgb[2];
    var grd = x.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, 'rgba(255,255,255,0.95)');
    grd.addColorStop(0.16, 'rgba(' + col + ',0.85)');
    grd.addColorStop(0.45, 'rgba(' + col + ',0.22)');
    grd.addColorStop(1, 'rgba(' + col + ',0)');
    x.fillStyle = grd;
    x.fillRect(0, 0, 64, 64);
    return c;
  }

  /* ---------- settings ---------- */

  /* One validated reader for every setting: out-of-range or unparseable values fall
     back to the default rather than wedging the field. 'auto' is spelled out. */
  function setting(el, opts, key, attr, dflt, min, max) {
    var v = opts[key] !== undefined ? opts[key] : el.getAttribute(attr);
    if (v === null || v === undefined || v === '' || v === 'auto') return dflt;
    var n = parseFloat(v);
    return isFinite(n) && n >= min && n <= max ? n : dflt;
  }

  function reducedMotion() { return !!(motionMq && motionMq.matches); }

  /* ---------- instance ---------- */

  function Instance(el, opts) {
    opts = opts || {};
    var data = window.CIRCUIT_BG_DATA;

    this.el = el;
    this.opts = opts;
    this.geom = prepare(data);
    this.addedClass = !el.classList.contains('circuit-bg');
    if (this.addedClass) el.classList.add('circuit-bg');

    /* scale: a number pins the tile; "auto" shrinks it in containers narrower than
       one tile, so a 390px card is as dense as a hero */
    this.fixedScale = setting(el, opts, 'scale', 'data-scale', 0, 0.05, 8);
    this.speed = setting(el, opts, 'speed', 'data-speed', 1, 0.05, 20);
    this.opacity = setting(el, opts, 'opacity', 'data-opacity', 1, 0, 1);
    var beams = setting(el, opts, 'beams', 'data-beams', -1, 0, BEAMS_MAX);
    this.beamsAttr = beams < 0 ? null : Math.round(beams);

    var colorOpt = opts.color !== undefined ? opts.color : el.getAttribute('data-color');
    var valid = colorOpt && (!window.CSS || !CSS.supports || CSS.supports('color', colorOpt));
    var rgb = beamColor(valid ? colorOpt : padGlow(data));
    this.rgb = rgb;
    this.color = 'rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')';
    this.beamPrefix = 'rgba(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ',';

    this.scale = this.fixedScale || 1;
    this.tileW = this.geom.tileW * this.scale;
    this.tileH = this.geom.tileH * this.scale;
    this.setTileVars();

    this.canvas = null;
    this.ctx = null;
    this.sprite = null;
    this.ro = null;
    this.io = null;
    this.raf = 0;
    this.last = 0;
    this.cssW = 0;
    this.cssH = 0;
    this.cols = 0;
    this.rows = 0;
    this.colCum = [];
    this.rowCum = [];
    this.dpr = 1;
    this.cap = 0;
    this.visible = false;     /* only the IntersectionObserver turns the loop on */
    this.beams = [];
    this.pool = [];
    this.busy = new Set();
    this.wait = [];           /* one spawn countdown per empty beam slot, ms */
    this.flashes = [];
    for (var i = 0; i < FLASH_MAX; i++) {
      this.flashes.push({ on: false, x: 0, y: 0, r: 0, t: 0, col: 0, row: 0 });
    }

    live.push(this);
    bindShared();

    var self = this;
    /* static tile only - but still watch the box, the tile scale is automatic */
    if (reducedMotion() || this.geom.startSum === 0 || this.beamsAttr === 0 || this.opacity === 0) {
      this.measure();
      this.ro = new ResizeObserver(function () { self.measure(); });
      this.ro.observe(el);
      return;
    }

    var canvas = document.createElement('canvas');
    canvas.className = 'circuit-bg__beams';
    canvas.setAttribute('aria-hidden', 'true');
    if (this.opacity !== 1) canvas.style.opacity = String(this.opacity);
    el.insertBefore(canvas, el.firstChild);
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.sprite = makeFlashSprite(rgb);

    this.measure();

    this.ro = new ResizeObserver(function () { self.measure(); });
    this.ro.observe(el);

    this.io = new IntersectionObserver(function (entries) {
      self.visible = entries[entries.length - 1].isIntersecting;
      self.resume();
    });
    this.io.observe(el);

    this.frame = function (now) {
      self.raf = 0;
      /* re-checked here, not only at the pause boundary: a missed observer callback
         must not be able to leave the loop running */
      if (!self.visible || document.hidden) { self.stop(); return; }
      var dt = self.last ? (now - self.last) / 1000 : 0;
      self.last = now;
      if (!(dt > 0)) dt = 0;
      if (dt > DT_MAX) dt = DT_MAX;
      /* ResizeObserver never fires when only the display density changes, and this
         check must not read layout: the CSS box is already known. */
      if (Math.min(2, window.devicePixelRatio || 1) !== self.dpr) self.backingStore();
      self.update(dt);
      self.draw();
      self.resume();
    };
  }

  Instance.prototype.setTileVars = function () {
    this.el.style.setProperty('--circuit-tile-w', round2(this.tileW) + 'px');
    this.el.style.setProperty('--circuit-tile-h', round2(this.tileH) + 'px');
  };

  /* The canvas is inset:0 inside the element, so it covers the padding box - which
     is also what background-position: 0 0 is measured from. clientWidth/Height are
     that box; contentRect and getBoundingClientRect are not.
     Only resize callbacks read layout; nothing in the frame loop does. */
  Instance.prototype.measure = function () {
    var el = this.el;
    var w = el.clientWidth, h = el.clientHeight;
    if (!w || !h) {
      var r = el.getBoundingClientRect();
      w = w || r.width;
      h = h || r.height;
    }
    this.resize(w, h);
  };

  Instance.prototype.autoScale = function (w) {
    if (this.fixedScale) return this.fixedScale;
    var s = Math.floor((w / this.geom.tileW) * 20) / 20;   /* 0.05 steps, never up */
    if (s > 1) s = 1;
    if (s < SCALE_MIN) s = SCALE_MIN;
    return s;
  };

  Instance.prototype.backingStore = function () {
    if (!this.canvas) return;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.max(1, Math.round(this.cssW * this.dpr));
    this.canvas.height = Math.max(1, Math.round(this.cssH * this.dpr));
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  };

  Instance.prototype.resize = function (w, h) {
    w = Math.max(0, Math.round(w));
    h = Math.max(0, Math.round(h));
    var scale = this.autoScale(w || 1);
    if (scale !== this.scale) {
      this.scale = scale;
      this.tileW = this.geom.tileW * scale;
      this.tileH = this.geom.tileH * scale;
      this.setTileVars();
    }
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    if (w === this.cssW && h === this.cssH && dpr === this.dpr) return;
    this.cssW = w; this.cssH = h;
    if (!this.canvas) return;
    this.backingStore();

    var cols = Math.max(1, Math.ceil(w / this.tileW));
    var rows = Math.max(1, Math.ceil(h / this.tileH));
    /* The last column/row is usually a sliver. Weighting the spawn by how much of
       each tile is on screen keeps the beams-per-visible-area constant whatever the
       container's aspect ratio, instead of spending the budget off the clipped edge. */
    this.colCum = visibleCum(cols, this.tileW, w);
    this.rowCum = visibleCum(rows, this.tileH, h);
    this.cap = this.beamsAttr !== null ? this.beamsAttr
      : (w < NARROW_W ? BEAMS_NARROW : BEAMS_DEFAULT);

    if (cols !== this.cols || rows !== this.rows) {
      this.cols = cols;
      this.rows = rows;
      /* beams and flashes keep their tile column/row, so a live window drag only
         re-homes them into the new grid instead of emptying the field */
      this.busy.clear();
      for (var i = this.beams.length - 1; i >= 0; i--) {
        var b = this.beams[i];
        b.col = b.col % cols;
        b.row = b.row % rows;
        if (!this.reserve(b)) this.drop(i);   /* it holds no key: retire would free another's */
      }
      for (i = 0; i < FLASH_MAX; i++) {
        var f = this.flashes[i];
        if (!f.on) continue;
        f.col = f.col % cols;
        f.row = f.row % rows;
      }
    }
    while (this.beams.length > this.cap) this.retire(this.beams.length - 1);
    this.resume();
  };

  /* Cumulative visible extent of each tile along one axis. */
  function visibleCum(count, tile, box) {
    var cum = new Array(count), acc = 0;
    for (var i = 0; i < count; i++) {
      var v = Math.min(tile, box - i * tile);
      acc += v > 0 ? v : 0;
      cum[i] = acc;
    }
    return cum;
  }

  function pickWeighted(cum) {
    var n = cum.length;
    if (n < 1) return -1;
    var total = cum[n - 1];
    if (!(total > 0)) return 0;
    var r = Math.random() * total;
    for (var i = 0; i < n; i++) if (cum[i] > r) return i;
    return n - 1;
  }

  Instance.prototype.stop = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.last = 0;          /* the first frame after a pause has elapsed 0, not a stale delta */
  };

  /* rAF is armed only while the instance is intersecting, the tab is visible and the
     box is non-zero - a display:none or collapsed instance burns no frames. */
  Instance.prototype.resume = function () {
    if (!this.canvas || !this.visible || document.hidden || !this.cssW || !this.cssH) {
      this.stop();
    } else if (!this.raf) {
      this.raf = requestAnimationFrame(this.frame);
    }
  };

  /* ---------- walks ---------- */

  Instance.prototype.key = function (col, row, ti) {
    return (row * this.cols + col) * this.geom.n + ti;
  };

  Instance.prototype.reserve = function (b) {
    var i;
    for (i = b.rel; i < b.nt; i++) if (this.busy.has(this.key(b.col, b.row, b.ti[i]))) return false;
    for (i = b.rel; i < b.nt; i++) this.busy.add(this.key(b.col, b.row, b.ti[i]));
    return true;
  };

  /* Plan one random walk in tile (col,row): a length-weighted start trace, a random
     direction, and at every node a random other trace incident to it - never the one
     it arrived on, never one already walked - until the target length is reached or
     the walk runs into a dead end or a wrap end. Returns the walked length. */
  Instance.prototype.walk = function (col, row) {
    var g = this.geom;
    var ti = pickStart(g);
    if (ti < 0) return 0;
    if (this.busy.has(this.key(col, row, ti))) return 0;
    var dir = Math.random() < 0.5 ? 1 : -1;
    var target = rand(WALK_MIN, WALK_MAX);
    var len = 0, n = 0;
    while (n < ROUTE_MAX) {
      tryTi[n] = ti; tryDir[n] = dir; walkSeen[n] = ti;
      len += g.total[ti];
      n++;
      if (len >= target) break;
      var node = dir > 0 ? g.nodeB[ti] : g.nodeA[ti];
      if (node < 0) break;                       /* wrap end, or an end with no node */
      var ts = g.nodes[node].ts, count = 0, next = -1, i, j;
      for (i = 0; i < ts.length; i++) {
        var c = ts[i];
        if (c === ti) continue;
        var seen = false;
        for (j = 0; j < n; j++) if (walkSeen[j] === c) { seen = true; break; }
        if (seen || this.busy.has(this.key(col, row, c))) continue;
        count++;
        if (Math.random() * count < 1) next = c;   /* reservoir pick: no array built */
      }
      if (next < 0) break;                       /* dead end */
      ti = next;
      dir = g.nodeA[ti] === node ? 1 : -1;
    }
    this.tryLen = len;
    this.tryN = n;
    return len;
  };

  /* Flatten the chosen route into one polyline so the trail crosses trace boundaries
     without knowing they are there, and record where the pad flashes fire. */
  Instance.prototype.materialize = function (b, n) {
    var g = this.geom, np = 0, acc = 0, i, j;
    b.nt = 0;
    b.nf = 0;
    b.fi = 0;
    b.rel = 0;
    for (i = 0; i < n; i++) {
      var ti = bestTi[i], dir = bestDir[i];
      var pts = g.pts[ti], m = g.cum[ti].length;
      if (np + m + 1 > ROUTE_PTS) break;    /* +1: the pad centre this trace may end on */
      b.ti[b.nt] = ti;
      for (j = 0; j < m; j++) {
        var k = dir > 0 ? j : m - 1 - j;
        var x = pts[k * 2], y = pts[k * 2 + 1];
        if (np > 0) {
          var dx = x - b.px[np * 2 - 2], dy = y - b.px[np * 2 - 1];
          acc += Math.sqrt(dx * dx + dy * dy);
        }
        b.px[np * 2] = x;
        b.px[np * 2 + 1] = y;
        b.pcum[np] = acc;
        np++;
      }
      b.nt++;
      /* the node this trace ends on: a pad there flashes as the head passes */
      var node = dir > 0 ? g.nodeB[ti] : g.nodeA[ti];
      var pad = node >= 0 ? g.nodes[node].pad : null;
      /* the trace ends are inset from the pad edge, so a walk that carries on through a
         pad steps through its centre - the beam crosses the pad it lights instead of
         cutting the corner around it */
      if (pad && i < n - 1) {
        var lx = b.px[np * 2 - 2], ly = b.px[np * 2 - 1];
        acc += Math.sqrt((pad.x - lx) * (pad.x - lx) + (pad.y - ly) * (pad.y - ly));
        b.px[np * 2] = pad.x;
        b.px[np * 2 + 1] = pad.y;
        b.pcum[np] = acc;
        np++;
      }
      b.tend[b.nt - 1] = acc;
      if (pad) {
        b.fs[b.nf] = acc;
        b.fp[b.nf] = pad;
        b.nf++;
      }
    }
    b.np = np;
    b.len = acc;
    return np >= 2;
  };

  Instance.prototype.spawn = function () {
    if (this.cols < 1 || this.rows < 1) return false;
    var col = pickWeighted(this.colCum), row = pickWeighted(this.rowCum);
    if (col < 0 || row < 0) return false;
    /* A dot-blip reads as a firefly, not as light travelling: keep re-planning until
       the route is long enough, then take the best attempt. */
    var bestLen = 0, bestN = 0, i;
    for (var t = 0; t < WALK_TRIES; t++) {
      var len = this.walk(col, row);
      if (len > bestLen) {
        bestLen = len; bestN = this.tryN;
        for (i = 0; i < bestN; i++) { bestTi[i] = tryTi[i]; bestDir[i] = tryDir[i]; }
      }
      if (bestLen >= WALK_FLOOR) break;
    }
    if (!bestN) return false;
    var b = this.pool.pop() || newBeam();
    b.col = col;
    b.row = row;
    if (!this.materialize(b, bestN)) { this.pool.push(b); return false; }
    b.head = 0;
    /* light travelling reads as a duration, not as a fixed px/s: pick how long this
       traverse should take, then the speed the route needs to fit it */
    var v = b.len / rand(DUR_MIN, DUR_MAX);
    b.speed = (v < SPEED_MIN ? SPEED_MIN : (v > SPEED_MAX ? SPEED_MAX : v)) * this.speed;
    /* a short route gets a short trail so the light visibly moves instead of lighting all at once */
    b.trail = b.len * TRAIL_FRAC;
    if (b.trail < TRAIL_MIN) b.trail = TRAIL_MIN;
    if (b.trail > TRAIL_LEN) b.trail = TRAIL_LEN;
    if (!this.reserve(b)) { this.pool.push(b); return false; }
    this.beams.push(b);
    return true;
  };

  function newBeam() {
    return {
      col: 0, row: 0, head: 0, speed: 0, len: 0, np: 0, nt: 0, nf: 0, fi: 0, rel: 0,
      px: new Float64Array(ROUTE_PTS * 2),
      pcum: new Float64Array(ROUTE_PTS),
      ti: new Int32Array(ROUTE_MAX),
      tend: new Float64Array(ROUTE_MAX),
      fs: new Float64Array(ROUTE_MAX),
      fp: new Array(ROUTE_MAX)
    };
  }

  /* Drops the beam without touching the busy set - for a beam that owns no
     reservation yet, where deleting its keys would free another beam's. */
  Instance.prototype.drop = function (i) {
    var b = this.beams[i];
    this.beams[i] = this.beams[this.beams.length - 1];
    this.beams.pop();
    this.pool.push(b);
  };

  Instance.prototype.retire = function (i) {
    var b = this.beams[i];
    for (var j = b.rel; j < b.nt; j++) this.busy.delete(this.key(b.col, b.row, b.ti[j]));
    b.rel = b.nt;
    this.drop(i);
  };

  Instance.prototype.flash = function (pad, col, row) {
    var f = null, oldest = this.flashes[0];
    for (var i = 0; i < FLASH_MAX; i++) {
      var c = this.flashes[i];
      if (!c.on) { f = c; break; }
      if (c.t > oldest.t) oldest = c;
    }
    if (!f) f = oldest;          /* never drop a flash: the oldest is nearly gone anyway */
    f.on = true; f.x = pad.x; f.y = pad.y; f.r = pad.r; f.t = 0; f.col = col; f.row = row;
  };

  /* Fixed sub-steps: spawn timing and arrivals stay correct whatever the frame rate. */
  Instance.prototype.update = function (dt) {
    while (dt > 0) {
      var h = dt > STEP ? STEP : dt;
      dt -= h;
      this.step(h);
    }
  };

  Instance.prototype.step = function (dt) {
    var i;
    /* One independent countdown per free slot rather than one timer for the field: with
       a single timer the beams that spawned together died together and left the box
       empty until the next tick. The cap counts every beam that is drawn, so a beam
       holds its slot while its tail drains off the end of its route, and the countdown
       is wall time - data-speed moves the beams, it does not spawn more of them. */
    var drawn = this.beams.length;
    while (drawn + this.wait.length < this.cap) this.wait.push(rand(SPAWN_MIN, SPAWN_MAX));
    while (this.wait.length && drawn + this.wait.length > this.cap) this.wait.pop();
    for (i = this.wait.length - 1; i >= 0; i--) {
      this.wait[i] -= dt * 1000;
      if (this.wait[i] > 0) continue;
      if (this.beams.length < this.cap && this.spawn()) {
        drawn++;
        this.wait[i] = this.wait[this.wait.length - 1];
        this.wait.pop();
      } else {
        this.wait[i] = rand(SPAWN_MIN, SPAWN_MAX);    /* no free route in that tile now */
      }
    }
    for (i = this.beams.length - 1; i >= 0; i--) {
      var b = this.beams[i];
      b.head += b.speed * dt;
      while (b.fi < b.nf && b.head >= b.fs[b.fi]) {
        this.flash(b.fp[b.fi], b.col, b.row);
        b.fi++;
      }
      /* free each trace as the tail clears it, so another beam can use it again */
      var tail = b.head - b.trail;
      while (b.rel < b.nt && b.tend[b.rel] <= tail) {
        this.busy.delete(this.key(b.col, b.row, b.ti[b.rel]));
        b.rel++;
      }
      /* let the tail drain off the end instead of popping the trail away */
      if (b.head >= b.len + b.trail) this.retire(i);
    }
    for (i = 0; i < FLASH_MAX; i++) {
      var f = this.flashes[i];
      if (f.on) { f.t += dt * 1000; if (f.t >= FLASH_MS) f.on = false; }
    }
  };

  /* One gradient along the head->tail chord, with a stop at every trail point, so
     the streak is a single continuous stroke rather than a row of alpha-stepped
     segments (round caps under 'lighter' bead at every joint). */
  function trailGradient(ctx, c, ox, oy, scale, prefix, gamma) {
    var hx = ox + trailBuf[0] * scale, hy = oy + trailBuf[1] * scale;
    var o = (c - 1) * 3;
    var dx = ox + trailBuf[o] * scale - hx, dy = oy + trailBuf[o + 1] * scale - hy;
    var len2 = dx * dx + dy * dy;
    if (len2 < 1) return null;
    var g = ctx.createLinearGradient(hx, hy, hx + dx, hy + dy);
    var prev = -1;
    for (var j = 0; j < c; j++) {
      var p = j * 3;
      var t = ((ox + trailBuf[p] * scale - hx) * dx + (oy + trailBuf[p + 1] * scale - hy) * dy) / len2;
      if (t < 0) t = 0; else if (t > 1) t = 1;
      if (t <= prev) t = prev + 0.002;
      if (t > 1) break;
      prev = t;
      var f = trailBuf[p + 2];
      var a = gamma === 2 ? f * f : f;
      g.addColorStop(t, prefix + a.toFixed(3) + ')');
    }
    return g;
  }

  Instance.prototype.draw = function () {
    var ctx = this.ctx;
    var scale = this.scale;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, this.cssW, this.cssH);
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    var i, j, c, o, ox, oy;

    for (i = 0; i < this.beams.length; i++) {
      var b = this.beams[i];
      var over = b.head - b.len;
      var head = over > 0 ? b.len : b.head;
      var len = over > 0 ? b.trail - over : b.trail;
      c = buildTrail(b.px, b.pcum, b.np, head, len);
      if (c < 2) continue;
      ox = b.col * this.tileW;
      oy = b.row * this.tileH;

      ctx.beginPath();
      ctx.moveTo(ox + trailBuf[0] * scale, oy + trailBuf[1] * scale);
      for (j = 1; j < c; j++) {
        o = j * 3;
        ctx.lineTo(ox + trailBuf[o] * scale, oy + trailBuf[o + 1] * scale);
      }

      /* the head's own trace sets the core width, so the beam keeps the weight of
         the run it is on as it crosses from a hairline to a thick trace */
      var cur = 0;
      while (cur < b.nt - 1 && b.tend[cur] < head) cur++;
      var core = this.geom.core[b.ti[cur]];
      var gGlow = trailGradient(ctx, c, ox, oy, scale, this.beamPrefix, 1);
      ctx.strokeStyle = gGlow || this.color;
      ctx.globalAlpha = BLOOM_A;
      ctx.lineWidth = (core + BLOOM_EXTRA) * scale;
      ctx.stroke();
      ctx.globalAlpha = GLOW_A;
      ctx.lineWidth = (core + GLOW_EXTRA) * scale;
      ctx.stroke();

      var gCore = trailGradient(ctx, c, ox, oy, scale, 'rgba(255,255,255,', 2);
      ctx.strokeStyle = gCore || '#ffffff';
      ctx.globalAlpha = 1;
      ctx.lineWidth = core * scale;
      ctx.stroke();

      var hr = HEAD_R * scale;
      ctx.globalAlpha = HEAD_A * (len / b.trail);
      ctx.drawImage(this.sprite,
        ox + trailBuf[0] * scale - hr, oy + trailBuf[1] * scale - hr, hr * 2, hr * 2);
    }

    for (i = 0; i < FLASH_MAX; i++) {
      var fl = this.flashes[i];
      if (!fl.on) continue;
      var u = 1 - fl.t / FLASH_MS;
      ctx.globalAlpha = u * u;
      var R = (FLASH_R_MUL * fl.r + FLASH_R_ADD) * scale;
      var fx = fl.col * this.tileW + fl.x * scale;
      var fy = fl.row * this.tileH + fl.y * scale;
      ctx.drawImage(this.sprite, fx - R, fy - R, R * 2, R * 2);
    }
    ctx.globalAlpha = 1;
  };

  /* Leaves the element exactly as it was found. */
  Instance.prototype.destroy = function () {
    this.stop();
    if (this.ro) { this.ro.disconnect(); this.ro = null; }
    if (this.io) { this.io.disconnect(); this.io = null; }
    if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
    this.el.style.removeProperty('--circuit-tile-w');
    this.el.style.removeProperty('--circuit-tile-h');
    if (this.addedClass) this.el.classList.remove('circuit-bg');
    this.canvas = null;
    this.ctx = null;
    this.sprite = null;
    this.frame = null;
    this.beams = [];
    this.pool = [];
    this.flashes = [];
    this.busy.clear();
    var i = live.indexOf(this);
    if (i >= 0) live.splice(i, 1);
    if (!live.length) unbindShared();
  };

  /* ---------- shared listeners ---------- */

  function onVisibility() {
    for (var i = live.length - 1; i >= 0; i--) live[i].resume();
  }

  function onMotion() {
    /* rebuild with or without the canvas; snapshot first, init() mutates live */
    var els = [], opts = [], i;
    for (i = 0; i < live.length; i++) { els.push(live[i].el); opts.push(live[i].opts); }
    for (i = 0; i < els.length; i++) init(els[i], opts[i]);
  }

  /* One listener for the whole page, not one per instance: a per-instance listener on
     document keeps every section the theme editor re-renders alive forever. */
  function bindShared() {
    if (sharedBound) return;
    sharedBound = true;
    document.addEventListener('visibilitychange', onVisibility);
    if (motionMq && motionMq.addEventListener) motionMq.addEventListener('change', onMotion);
    else if (motionMq && motionMq.addListener) motionMq.addListener(onMotion);   /* Safari < 14 */
  }

  function unbindShared() {
    if (!sharedBound) return;
    sharedBound = false;
    document.removeEventListener('visibilitychange', onVisibility);
    if (motionMq && motionMq.removeEventListener) motionMq.removeEventListener('change', onMotion);
    else if (motionMq && motionMq.removeListener) motionMq.removeListener(onMotion);
  }

  /* An element removed from the DOM takes its instance with it only if nothing holds
     a reference; live[] does, so drop the detached ones whenever we touch the list. */
  function sweep() {
    for (var i = live.length - 1; i >= 0; i--) {
      if (!live[i].el.isConnected) destroy(live[i].el);
    }
  }

  /* ---------- public API ---------- */

  function init(el, opts) {
    if (!el) return null;
    var data = window.CIRCUIT_BG_DATA;
    if (!data || !(data.width > 0) || !(data.height > 0)) {
      throw new Error('CircuitBackground requires circuit-data.js before initialization.');
    }
    sweep();
    destroy(el);
    var inst = new Instance(el, opts);
    instances.set(el, inst);
    return inst;
  }

  function destroy(el) {
    var inst = instances.get(el);
    if (inst) { inst.destroy(); instances.delete(el); }
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-circuit-bg]');
    /* one bad element must not abort the rest of the page */
    for (var i = 0; i < nodes.length; i++) {
      if (instances.has(nodes[i])) continue;      /* leave anything already initialised alone */
      try { init(nodes[i]); } catch (e) { console.error(e); }
    }
  }

  window.CircuitBackground = { init: init, destroy: destroy, version: 2 };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
